import { Grid, GridColumn as Column, GridColumnMenuProps, GridColumnMenuCheckboxFilter, GridCellProps, GridToolbar, GridColumn, GridColumnMenuFilter, GridNoRecords, cellOperatorChange } from '@progress/kendo-react-grid';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { AccessToken } from '../config/AccessToken';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate } from 'react-router-dom';
import { arrowRotateCwIcon, fileCsvIcon, fileExcelIcon, filePdfIcon, saveIcon, uploadIcon } from '@progress/kendo-svg-icons';
import { process as kendoProcess } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';
import { CSVLink } from 'react-csv';
import { json2csv } from 'json-2-csv';
import { loadingPanel } from '../components/LoadingPanel';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { ConfiguratorToggleColumnsGrid } from '../components/ConfiguratorToggleColumnsGrid';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

/**
 * A cell component that displays a button to navigate to the map
 * view for the associated beacon.
 *
 * When clicked, it saves the beacon's id and map id in local storage
 * and navigates to the map view.
 *
 * @param {Object} props - The properties passed to the component.
 */
const LinkMapCell = (props: any) => {

  const localizationService = useLocalization();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [instance, accessTokenRequest] = AccessToken();

  const beaconId = props?.dataItem?.id;
  const [mapIdByBeacon, setMapIdByBeacon] = useState<any>(null);

  useEffect(() => {
    const getMapIdByBeacon = async (beaconId: any) => {

      let languageId = localStorage.getItem('languageId');

      const commonOperations = async (accessToken: any) => {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

        await axios.get(`${SERVER_URL}/beacon/${beaconId}/map`, { headers }).then((res: any) => {

          const mapId = res?.data?.data?.id;
          setMapIdByBeacon(mapId);

        }).catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        })
      }

      if (localStorage.getItem('accessToken') != null) {
        let accessToken = localStorage.getItem('accessToken');
        commonOperations(accessToken);
      } else {
        await instance.initialize();
        await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
          // Acquire token silent success
          let accessToken = accessTokenResponse.accessToken;
          commonOperations(accessToken)
        })
      }
    }
    if (beaconId) {
      getMapIdByBeacon(beaconId);
    }
  }, [beaconId, instance, accessTokenRequest])


  const navigate = useNavigate()
  const showMap = () => {
    if (beaconId) {
      localStorage.setItem("beaconId", beaconId);
    }
    if (mapIdByBeacon) {
      localStorage.setItem("mapIdByBeacon", mapIdByBeacon);
    }

    localStorage.removeItem("deviceMapIdFromSynoptic")
    localStorage.removeItem("deviceMacFromSynoptic")

    navigate("/home/map")
  }

  const isMapEnabled = mapIdByBeacon != null;
  const mapButtonTitle = mapIdByBeacon != null
    ? localizationService.toLanguageString('beacons.map', 'NOT_FOUND')
    : localizationService.toLanguageString('beacons.no_position_found', 'NOT_FOUND');


  return (
    <td className="k-command-cell" style={{ textAlign: 'center' }}>
      <button
        id="map"
        type="button"
        aria-label={mapButtonTitle}
        className="k-icon k-font-icon k-i-globe beacon-globe beacon-cell-action"
        style={{
          color: '#0f6671',
          margin: '6px',
          padding: '12px',
          borderRadius: '999px',
          border: 'none',
          background: '#e8f5f7',
          outline: 'none',
          cursor: isMapEnabled ? 'pointer' : 'not-allowed',
          opacity: isMapEnabled ? 1 : 0.4,
          boxShadow: isMapEnabled ? '0 2px 8px rgba(11, 74, 84, 0.2)' : 'none'
        }}
        title={mapButtonTitle}
        disabled={!isMapEnabled}
        onClick={isMapEnabled ? showMap : undefined}
      />
    </td>

  );
};

interface PageState {
  skip: number;
  take: number;
}

//const initialDataState: PageState = { skip: 1, take: 10 };
const initialDataState: PageState = { skip: 0, take: 10 };

/**
 * The `Beacons` component is responsible for displaying and managing a grid of
 * beacon devices. It retrieves beacon data from a server, displays it in a grid,
 * and provides functionalities such as filtering, sorting, exporting to Excel and PDF,
 * and state persistence in local storage.
 *
 * The grid supports column configuration, allowing users to toggle visibility,
 * reorder, and resize columns. It also highlights search text within the grid cells
 * and provides a command cell for navigation to a map view.
 *
 * The component manages both online and offline beacon data, displaying battery
 * status with color coding. It uses localization services for translating column titles
 * and other UI elements.
 *
 * Beacons data is fetched using an API call, and the component handles loading and error
 * states during this process. The component's state is initialized and updated using
 * React hooks.
 *
 * @returns {JSX.Element} The JSX code for rendering the Beacons grid component.
 */
export const Beacons = () => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  //const [processedData, setProcessedData] = useState<any>([])

  const [columns, setColumns] = useState<any>([])

  const [beacons, setBeacons] = useState<any>([]);
  const [beaconsOn, setBeaconsOn] = useState<any>([]);
  const [countBeaconsOn, setCountBeaconsOn] = useState(0);
  const [beaconsOff, setBeaconsOff] = useState([]);
  const [error, setError] = useState('');


  const [pageIndex, setPageIndex] = useState<any>(initialDataState);
  const [totalItems, setTotalItems] = useState<number>();
  const [pageSizeValue, setPageSizeValue] = useState()

  const [dateValue, setDateValue] = useState<Date | null>(new Date());

  const [filterName, setFilterName] = useState<any>(() => {
    const filterNameBeacon = localStorage.getItem('filterNameBeacon');
    return filterNameBeacon ? JSON.parse(filterNameBeacon) : [];
  });

  const [loading, setLoading] = useState(false);

  const localizationService = useLocalization();

  const [instance, accessTokenRequest] = AccessToken();

  const userEmail = localStorage.getItem('email');

  const languageId = localStorage.getItem('languageId');

  const convertDateTime = (dateTime: any) => {

    const [datePart, timePart] = dateTime.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour, minute, seconds] = timePart.split(':');

    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

    return date
  }

  const getBeaconList = async (flag: boolean) => {

    let languageId = localStorage.getItem('languageId');
    const apiLoadErrorMessage = localizationService.toLanguageString(
      'beacons.load_error',
      languageId === 'it-IT' ? 'Impossibile caricare i beacons' : 'Unable to load beacons'
    );
    setError('');
    setLoading(true)

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const params = {
        enabled: flag
      };
      await axios.get(`${SERVER_URL}/beacon/list`, { headers, params }).then((res: any) => {

        const data: any = res?.data?.data

        let beaconOn: any = []

        if (flag) {
          setCountBeaconsOn(data?.length)
          // format date in data.data
          data?.forEach((beacon: any) => {
            if (beacon?.lastSeen == null) {
              beacon.lastSeen = localizationService.toLanguageString('beacons.date_not_found', 'NOT_FOUND')
              return
            }
            beacon.lastSeen = convertDateTime(beacon?.lastSeen)

            beaconOn.push(beacon)
          })

          setBeaconsOn(beaconOn);
          setBeacons(data)

          //setTotalItems(data?.totalItems)

          loadStateFromLocalStorage()

        } else {
          setBeaconsOff(data);
        }
        setLoading(false)
      }).catch((error: any) => {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
          return;
        }
        setBeaconsOn([]);
        setBeacons([]);
        setCountBeaconsOn(0);
        setError(apiLoadErrorMessage);
        loadStateFromLocalStorage();
        setLoading(false)
      })
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken)
      })
    }
  }

  const getBeaconPaginatedList = async (flag: boolean, skip?: any, take?: any) => {

    let languageId = localStorage.getItem('languageId');
    const apiLoadErrorMessage = localizationService.toLanguageString(
      'beacons.load_error',
      languageId === 'it-IT' ? 'Impossibile caricare i beacons' : 'Unable to load beacons'
    );
    setError('');
    setLoading(true)

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const params = {
        enabled: flag,
        pageIndex: skip ?? pageIndex?.skip,
        pageSize: take ?? pageIndex?.take
      };
      try {
        const res: any = await axios.get(`${SERVER_URL}/beacon/paginatedlist`, { headers, params })

        const data: any = res.data.data

        if (data == null || data == '' || data.items.length == 0) {
          setLoading(false);
          return;
        }

        let beaconOn: any = []

        if (flag) {
          setCountBeaconsOn(data?.length)
          // format date in data.data
          data?.items?.forEach((beacon: any) => {
            if (beacon?.lastSeen == null) {
              beacon.lastSeen = localizationService.toLanguageString('beacons.date_not_found', 'NOT_FOUND')
              return
            }
            beacon.lastSeen = convertDateTime(beacon?.lastSeen)

            if (beacon.name == null || beacon.name == '') beacon.name = localizationService.toLanguageString('beacons.empty', 'NOT_FOUND')
            if (beacon.type == null || beacon.type == '') beacon.type = localizationService.toLanguageString('beacons.empty', 'NOT_FOUND')

            beaconOn.push(beacon)
          })

          setBeaconsOn(beaconOn);
          setBeacons(data?.totalItems)

          setTotalItems(data?.totalItems)

          loadStateFromLocalStorage()

        } else {
          setBeaconsOff(data.items);
        }
        setLoading(false)
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
          return;
        }
        setBeaconsOn([]);
        setBeacons([]);
        setCountBeaconsOn(0);
        setError(apiLoadErrorMessage);
        loadStateFromLocalStorage();
        setLoading(false)
      }
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken)
      })
    }
  }

  useEffect(() => {
    loadStateFromLocalStorage()
    getBeaconList(true)
    //getBeaconPaginatedList(true)
  }, [])

  const exportRecords = async (format: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      axios.get(`${SERVER_URL}/beacon/download?format=${format}`, { headers, responseType: 'blob' })
        .then((response) => {

          const type: any = format == 'csv' ? 'text/csv;charset=utf-8;' : format == 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'

          const ext: any = format == 'csv' ? '.csv' : format == 'excel' ? '.xlsx' : '.pdf'

          const blob = new Blob([response.data], { type: type });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", `records${ext}`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setLoading(false);

        }).catch((error) => {
          if (error.response) {
            setError(error.response.data.message);
          } else {
            setError(error.message);
          }
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: pageIndex?.take ?? 10,
    data: beacons,
    total: beacons?.length,
    filter: { logic: 'and', filters: [] },
    //sort: [{ field: 'id', dir: 'desc' }]
  });

  const filteredGridFields = useMemo(() => {
    const fields = new Set<string>();

    const collectFields = (filter: any): void => {
      if (!filter || !Array.isArray(filter.filters)) {
        return;
      }

      filter.filters.forEach((entry: any) => {
        if (entry && Array.isArray(entry.filters)) {
          collectFields(entry);
          return;
        }

        const field = typeof entry?.field === 'string' ? entry.field.trim() : '';
        if (field.length > 0) {
          fields.add(field);
        }
      });
    };

    collectFields(dataState?.filter);
    return Array.from(fields);
  }, [dataState?.filter]);

  const hasActiveGridFilters = filteredGridFields.length > 0;

  const sanitizeFilter = (filter: any) => {
    if (filter && typeof filter === 'object' && Array.isArray(filter.filters)) {
      const flatFilters = filter.filters.flatMap((f: any) =>
        Array.isArray(f.filters) ? f.filters : [f]
      );

      return {
        logic: filter.logic || 'and',
        filters: flatFilters
      };
    }
    return { logic: 'and', filters: [] };
  };

  const safeFilter = sanitizeFilter(dataState?.filter);
  const [processedData, setProcessedData] = useState<any>({ data: [], total: 0 });


  useEffect(() => {
    //if (!devicesOn || devicesOn.length === 0) return;

    const result = kendoProcess(beaconsOn, {
      filter: dataState?.filter ?? safeFilter,
      sort: dataState?.sort,
      skip: dataState?.skip,
      take: dataState?.take,
    });

    setProcessedData(result);

    if (result.data.length === 0 && result.total > 0 && dataState.skip > 0 && !firstLoad) {
      setDataState((prev: any) => ({ ...prev, skip: 0 }));
    }
  }, [beaconsOn, dataState?.filter, dataState?.sort, dataState?.skip, dataState?.take]);

  const onPageChange = (event: any) => {

    const allItems: any = localizationService.toLanguageString('beacons.all', 'NOT_FOUND')
    const targetEvent = event.targetEvent;
    const newSkip = event?.page?.skip;
    const newTake = event?.targetEvent?.value === allItems ? beaconsOn?.length : event.page.take;
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setDataState((prevState: any) => ({
      ...prevState,
      skip: newSkip,
      take: newTake
    }));
    //const updatedState = {
    //    dataState: {
    //        ...dataState,
    //        skip: newSkip,
    //        take: newTake
    //    },
    //    columns
    //};
    //localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify(updatedState));
  };

  const removeFilterByField = (filterObject: any, fieldName: any): any => {
    if (!filterObject || !filterObject.filters || !Array.isArray(filterObject.filters)) {
      return { logic: 'and', filters: [] };
    }

    const updatedFilters = filterObject.filters
      .map((item: any) => {
        // Se è il filtro da rimuovere
        if (item.field === fieldName) {
          return null;
        }

        // Se è un gruppo di filtri annidato
        if (item.filters && Array.isArray(item.filters)) {
          const updatedGroup = removeFilterByField(item, fieldName);
          // Mantieni solo se il gruppo ha ancora filtri
          if (updatedGroup.filters.length > 0) {
            return updatedGroup;
          } else {
            return null;
          }
        }
        return item;
      })
      .filter((item: any) => item !== null);

    // Sempre restituire un oggetto con 'logic' e 'filters'
    return {
      logic: filterObject.logic || 'and',
      filters: updatedFilters
    };
  };

  const ColumnMenu = (props: any) => {

    const {
      firstFilterProps,
      hideSecondFilter,
      secondFilterProps,
      logicData,
      logicValue,
      onLogicChange,
    } = props;

    const customFilterOperators: any = {
      date: [
        { text: 'grid.filterNotEqOperator', operator: 'neq' },
        { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
        { text: 'grid.filterAfterOperator', operator: 'gt' },
        { text: 'grid.filterBeforeOperator', operator: 'lt' },
        { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
        //{ text: 'grid.filterIsNullOperator', operator: 'isnull' },
        //{ text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' }
      ]
    };

    const handleFilterChange = (filterValue: any) => {
      setDataState((prevState: any) => ({
        ...prevState,
        filter: filterValue
      }));
      let filterName: any = []
      filterValue?.filters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameBeacon", JSON.stringify(filterName));
    };

    return (
      <div>
        <GridColumnMenuFilterCell
          {...firstFilterProps}
          alwaysExpand
          //filterOperators={customFilterOperators}
          onFilterChange={handleFilterChange}
        />
      </div>
    );
  }

  const ColumnMenuCheckboxFilter = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuCheckboxFilter {...props} data={beaconsOn} expanded={true} searchBoxFilterOperator={'contains'} />
      </div>
    );
  }

  const ColumnMenuNumericFilter = (props: any) => {

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    useEffect(() => {
      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);

      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameBeacon', JSON.stringify(updated));
        return updated;
      });

      localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    if (!props || !props.column) {
      return null;
    }

    const customFilterOperators: any = {
      numeric: [
        { text: 'grid.filterEqOperator', operator: 'eq' },
        { text: 'grid.filterNotEqOperator', operator: 'neq' },
        { text: 'grid.filterGteOperator', operator: 'gte' },
        { text: 'grid.filterGtOperator', operator: 'gt' },
        { text: 'grid.filterLteOperator', operator: 'lte' },
        { text: 'grid.filterLtOperator', operator: 'lt' }

      ]
    };

    const handleFilterChange = (filterValue: any) => {

      const newFilter: any = filterValue
        ? { ...filterValue }
        : { logic: 'and', filters: [] };

      const newSkip = dataState.skip ?? 0;
      const newTake = dataState.take ?? 10;

      let filterName: any = []
      filterValue?.filters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameBeacon", JSON.stringify(filterName));

      if (dataState) {
        const { sort } = dataState;

        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter,
          sort: sort,
          skip: 0,
          take: newTake
        }));
      }

      // Salva lo stato aggiornato completo nel localStorage
      const updatedState = {
        dataState: {
          ...dataState,
          filter: newFilter,
          skip: newSkip,
          take: newTake
        },
        columns
      };
      localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify(updatedState));

    };

    return (
      <GridColumnMenuFilter
        {...props}
        expanded={true}
        hideSecondFilter={true}
        filterOperators={customFilterOperators}
        onFilterChange={handleFilterChange}
      />
    );
  };

  const BatteryCellWithBackGroundComponent = (props: any) => {
    const batteryValue = props.dataItem.battery;
    //const batteryValueWithoutPercentage = batteryValue.trim().replace('%', '');

    if (!batteryValue) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>?</title>
            <g transform="rotate(90 32 32)">
              <g fill="black">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="black">
                <text
                  x="35"
                  y="45"
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="bold"
                  fill="black"
                  transform="rotate(-90 32 38)"
                >
                  ?
                </text>
              </g>
            </g>
          </svg>
        </td>
      )
    }
    else if (batteryValue < 25) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="red">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    }
    else if (batteryValue >= 25 && batteryValue <= 50) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="yellow">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    } else if (batteryValue > 50 && batteryValue <= 75) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="lightgreen">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    } else {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="green">
                <path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    }
  };

  const currColumns: any = [
    { field: 'id', title: localizationService.toLanguageString('beacons.id', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true },
    { field: 'name', title: localizationService.toLanguageString('beacons.name', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true },
    { field: 'type', title: localizationService.toLanguageString('beacons.type', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true },
    { field: 'lastSeen', title: localizationService.toLanguageString('beacons.lastSeen', 'NOT_FOUND'), filter: 'date', format: '{0:dd/MM/yyyy HH:mm:ss}', filterable: true, editable: false, show: true },
    { field: 'battery', title: localizationService.toLanguageString('beacons.battery', 'NOT_FOUND'), filter: 'numeric', cell: BatteryCellWithBackGroundComponent, filterable: true, editable: false, show: true }
  ];



  const saveStateToLocalStorage = () => {
    const state: any = { dataState, columns };
    localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify(state));
  };

  const visibleCountBeacon = JSON.parse(localStorage.getItem("visibleCountBeacon") || "0");
  const [firstLoad, setFirstLoad] = useState(true);


  const loadStateFromLocalStorage = () => {
    const storedFilter = localStorage.getItem(`gridBeaconState: ${userEmail}`);
    if (storedFilter) {
      const { dataState: savedDataState, columns: savedColumns } = JSON.parse(storedFilter);

      savedColumns.forEach((column: any) => {
        column.title = localizationService.toLanguageString(`beacons.${column.field}`, 'NOT_FOUND');
      })

      if (savedDataState?.filter != null) {
        // Funzione ricorsiva per gestire qualsiasi struttura di filtri
        const updateFilterValues = (filter: any): any => {
          if (Array.isArray(filter.filters)) {
            return {
              ...filter,
              filters: filter.filters.map(updateFilterValues),
            };
          } else {
            const field = filter?.field;
            let value = filter?.value;

            if (['lastSeen'].includes(field)) {
              value = new Date(value);
            }

            return {
              ...filter,
              value,
            };
          }
        };

        const updatedFilter = updateFilterValues(savedDataState.filter);

        const updatedSavedDataState = {
          ...savedDataState,
          filter: updatedFilter,
        };

        setDataState(updatedSavedDataState);
      } else {
        setDataState(savedDataState);
      }

      if (visibleCountBeacon === 0 && !firstLoad) {

        const storedState = JSON.parse(localStorage.getItem(`gridBeaconState: ${userEmail}`) || "{}");

        const updatedState = {
          ...storedState,
          dataState: {
            ...storedState.dataState,
            skip: 0
          }
        };

        localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify(updatedState));
      }

      setFirstLoad(false)

      let currentColumn: any = savedColumns?.map((column: any, id: any) => {
        if (column.id == id) {
          return { ...column, show: !column.show }; // Create a *new* object
        }
        return column; // Return the other columns unchanged
      });

      setColumns(currentColumn)

      return storedFilter
        ? storedFilter
        : currentColumn;
    } else {
      setColumns(currColumns);
    }
  };

  const onChange = (event: any) => {
    const field = event.target.name;
    const newColumns = columns.map((column: any) => {
      if (column.field === field) {
        return { ...column, show: !column.show };
      }
      return column;
    });
    setColumns(newColumns);

    const state = { dataState, columns: newColumns };
    localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify(state));
  };

  const onColumnReorder = (event: any) => {
    setColumns(event.columns);
    saveStateToLocalStorage();
  };

  const onColumnResize = (event: any) => {
    setColumns(event.columns);
    saveStateToLocalStorage();
  };

  const onDataStateChange = (event: any) => {

    const newFilter: any = event.dataState.filter
      ? { ...event.dataState.filter }
      : { logic: 'and', filters: [] };

    setDataState((prevState: any) => ({
      ...prevState,
      filter: newFilter
    }));

    let filterName: any = []
    newFilter?.filters?.forEach((filter: any) => {
      filter?.filters?.forEach((filterInfo: any) => {
        filterName.push(filterInfo.field)
      })
    })
    setFilterName(filterName)
    localStorage.setItem("filterNameBeacon", JSON.stringify(filterName));

    const { dataState } = event;
    if (dataState) {
      const { filter, sort, skip, take } = dataState;
      const updatedFilter = filter || dataState.filter;
      const updatedSkip = skip !== undefined ? skip : dataState.skip;
      const updatedTake = take !== undefined ? take : dataState.take;
      setDataState((prevState: any) => ({
        ...prevState,
        filter: updatedFilter,
        sort: sort,
        skip: updatedSkip,
        take: updatedTake
      }));
    }

    const state = { dataState, columns };
    localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify(state));
  };

  const CellRender = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
      >
        {props.children}
      </td>
    );
  }

  //useEffect(() => {
  //  const result = kendoProcess(beaconsOn ?? [], {
  //      filter: dataState?.filter,
  //      sort: dataState?.sort,
  //      skip: dataState?.skip,
  //      take: pageIndex?.take ?? dataState?.take,
  //  });

  //  setProcessedData(result.data);
  //  if(filterName.length != 0){
  //    setTotalItems(result.total);
  //  }else{
  //    setTotalItems(beacons);
  //  }

  //}, [beaconsOn,filterName]);

  //const processedData: any = kendoProcess(beaconsOn, {
  //  filter: dataState?.filter,
  //  sort: dataState?.sort,
  //  skip: dataState?.skip,
  //  take: dataState?.take,
  //});

  const formatDateFromIsoToString = (date: any) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (languageId == 'en-US') {
      hours = hours % 12;
      hours = hours === 0 ? 12 : hours; // convert 0 → 12
      const hours12 = String(hours).padStart(2, '0');
      return `${month}/${day}/${year} ${hours12}:${minutes}:${seconds}`;
    } else {
      const hours24 = String(hours).padStart(2, '0');
      return `${day}/${month}/${year} ${hours24}:${minutes}:${seconds}`;
    }
  }

  const DateTimeCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.lastSeen);
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const lastSeenValue: any = formatDateFromIsoToString(date);
    if (lastSeenValue != null && !lastSeenValue.includes('1970')) {
      return (
        <td>
          <span className='k-table-row k-master-row date-time'>
            {lastSeenValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row date-time' /></td>
      )
    }
  };

  const GridColumnMenuFilterCell = (props: any) => {

    const gridColumnMenuFilterCell = useRef<any>(null);

    const handleResetClick = () => {
      const field = props.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);
      //setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameBeacon', JSON.stringify(updated));
        return updated;
      });
      localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      //localStorage.setItem('filterNameBeacon', JSON.stringify([]));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    const [selectedOperator, setSelectedOperator] = useState(props.operator || 'eq');

    const containsDateTimeField = (filters: any): boolean => {
      if (!Array.isArray(filters)) return false;

      return filters.some((filter: any) => {
        if (filter.field === 'dateTime') {
          return true;
        }
        // Se ha sotto-filtri, ricorsivamente cerca anche lì
        if (Array.isArray(filter.filters)) {
          return containsDateTimeField(filter.filters);
        }
        return false;
      });
    };

    const [tempDateTime, setTempDateTime] = useState<Date | any>(null);

    const handleTempDateTimeChange = (value: Date | null) => {
      setTempDateTime(value);
    };

    const operatorChange = (event: any) => {
      const newOperator = event.value.operator;
      setSelectedOperator(newOperator); // <-- Track selected operator
      cellOperatorChange(event, props.value, props.onChange);
    };

    const renderOperatorEditor = () => {
      const { operators } = props;

      const filteredOperators = operators.filter(
        (op: any) => op.operator !== 'isnull' && op.operator !== 'isnotnull'
      );

      const value = filteredOperators.find((item: any) => item.operator === selectedOperator) || null;

      return (
        <DropDownList
          value={value}
          onChange={operatorChange}
          data={filteredOperators}
          textField="text"
        />
      );
    };

    useEffect(() => {
      if (!tempDateTime) return;

      // Serve solo a far vedere a Kendo che c'è un valore
      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: new Date(tempDateTime.setMilliseconds(0))
      });
    }, [tempDateTime]);


    useEffect(() => {
      if (props.value && !tempDateTime) {
        setTempDateTime(new Date(props.value));
      }
    }, [props.value]);


    const applyDateTimeFilter = () => {
      if (!tempDateTime) return;

      const cleanDate = new Date(tempDateTime);
      cleanDate.setMilliseconds(0);

      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: cleanDate
      });

      setFilterName((prev: any) => {
        if (!prev.includes(props.field)) return [...prev, props.field];
        return prev;
      });

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: [
            {
              logic: 'or',
              filters: [
                {
                  field: props.field,
                  operator: selectedOperator,
                  value: cleanDate
                }
              ]
            }
          ]
        }
      };

      setDataState(newState);

      localStorage.setItem(`gridBeaconState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.setItem('filterNameBeacon', JSON.stringify([props.field]));
      localStorage.removeItem('filterOpenedColumn');
      getBeaconPaginatedList(true);
    };

    useEffect(() => {
      const handleDocumentClick = (e: MouseEvent) => {
        const resetButton = (e.target as HTMLElement).closest('button[type="reset"]');
        const applyButton = (e.target as HTMLElement).closest('button.k-button');

        const resetText = resetButton?.textContent?.trim();
        const applyText = applyButton?.textContent?.trim();

        if (resetText === 'Ripulisci' || resetText === 'Clear') {
          handleResetClick();
        } else if (applyText === 'Filtro' || applyText === 'Filter') {
          applyDateTimeFilter();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, [tempDateTime, selectedOperator]);

    return (
      <div
        ref={gridColumnMenuFilterCell}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ zIndex: 10000, position: 'relative' }}
      >
        {renderOperatorEditor()}

        <DatePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

        <div style={{ marginBottom: '10px' }}></div>

        <TimePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'hh:mm:ss a' : 'HH:mm:ss'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

      </div>
    );
  };
  const CustomGridFilterDateTime = (props: any) => {
    const {
      firstFilterProps
    } = props;


    return (
      <div>
        <GridColumnMenuFilterCell
          {...firstFilterProps}
        />
      </div>
    );
  };

  const _excelExport = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_excelExport.current !== null) {
      _excelExport.current.save();
    }
  };

  const _pdfExport = useRef<GridPDFExport | null>(null);
  const pdfExport = () => {
    if (_pdfExport.current) {
      if (beaconsOn.length < 500) {
        _pdfExport.current.save(beaconsOn);
      } else {
        _pdfExport.current.save();
      }
    }
  };


  const IconHeaderCell = (props: any) => {
    const field = typeof props?.field === 'string' ? props.field : '';
    const isFilteredColumn = field !== '' && filteredGridFields.includes(field);
    const mergedThProps = {
      ...(props?.thProps ?? {}),
      className: `${props?.thProps?.className ?? ''}${isFilteredColumn ? ' beacon-filtered-header' : ''}`.trim()
    };

    return (
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...mergedThProps}>
        {props.children}
      </HeaderThElement>
    );
  };

  const beaconSurfaceStyle = {
    position: 'relative' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    width: 'calc(100% - 20px)',
    height: 'calc(100% - 10px)',
    minHeight: 0,
    overflow: 'hidden' as const,
    boxSizing: 'border-box' as const,
    margin: '10px 10px 10px 10px',
    borderRadius: '12px',
    background: 'linear-gradient(180deg, #f5fbfc 0%, #eaf5f7 100%)',
    border: '1px solid #bdd8dd',
    boxShadow: '0 14px 30px rgba(9, 64, 72, 0.2)'
  };

  const toolbarStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    height: '75px',
    minHeight: '75px',
    maxHeight: '75px',
    flex: '0 0 75px',
    boxSizing: 'border-box' as const,
    alignItems: 'center',
    borderRadius: '10px 10px 0px 0px',
    background: 'linear-gradient(180deg, #d8edf1 0%, #cfe8ed 100%)',
    border: 'none',
    borderBottom: '1px solid #9ec8cf'
  };

  const toolbarActionsStyle = {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    padding: '4px',
    borderRadius: '10px',
    backgroundColor: '#ecf7f9'
  };

  const toolbarButtonStyle = {
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(10, 74, 84, 0.18)'
  };

  const gridWrapperStyle = {
    position: 'relative' as const
  };

  const onRefreshGrid = () => {
    getBeaconList(true);
  };

  const beaconMotionCss = `
    @keyframes beaconFadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes beaconPopIn {
      from { opacity: 0; transform: scale(0.98); }
      to { opacity: 1; transform: scale(1); }
    }

    .beacon-animated-surface {
      animation: beaconFadeUp 260ms ease-out both;
    }

    .drawer-layout-shell:has(.beacon-animated-surface) {
      height: calc(100vh - 132px);
      min-height: 0;
    }

    .drawer-content-shell:has(.beacon-animated-surface) {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .drawer-content-shell:has(.beacon-animated-surface) > .beacon-animated-surface {
      flex: 1 1 auto;
      min-height: 0;
    }

    .beacon-grid-colorized {
      display: flex;
      flex-direction: column;
      flex: 1 1 0;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .beacon-grid-colorized .k-grid-toolbar,
    .beacon-grid-colorized .k-grid-header,
    .beacon-grid-colorized .k-grid-pager {
      flex: 0 0 auto;
    }

    .beacon-grid-colorized .k-grid-header,
    .beacon-grid-colorized .k-grid-header-wrap {
      overflow: hidden;
      overflow-x: hidden;
      overflow-y: hidden;
    }

    .beacon-grid-colorized .k-grid-container {
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .beacon-grid-colorized .k-grid-aria-root {
      flex: 1 1 auto;
      min-height: 0;
    }

    .beacon-grid-colorized .k-grid-content {
      flex: 1 1 0;
      height: 100%;
      min-height: 0;
      overflow-x: auto;
      overflow-y: scroll;
    }

    .beacon-grid-colorized .k-grid-table-wrap {
      height: 100%;
      min-height: 100%;
    }

    .beacon-grid-colorized .k-table.k-grid-table.k-table-md {
      height: 100%;
    }

    .beacon-animated-toolbar {
      animation: beaconPopIn 320ms ease-out both;
    }

    .beacon-animated-toolbar-secondary {
      animation-delay: 80ms;
    }

    .beacon-toolbar-actions {
      transition: box-shadow 200ms ease;
    }

    .beacon-toolbar-actions:hover {
      box-shadow: inset 0 0 0 1px rgba(13, 90, 102, 0.16);
    }

    .beacon-action-btn {
      transition: transform 170ms ease, box-shadow 170ms ease, filter 170ms ease;
    }

    .beacon-action-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(8, 65, 73, 0.24);
      filter: saturate(1.05);
    }

    .beacon-action-btn:focus-visible,
    .beacon-cell-action:focus-visible {
      outline: 2px solid #0f6671;
      outline-offset: 2px;
    }

    .beacon-cell-action {
      transition: transform 170ms ease, box-shadow 170ms ease, background-color 170ms ease;
    }

    .beacon-cell-action:hover {
      transform: scale(1.3);
      box-shadow: 0 4px 12px rgba(11, 74, 84, 0.26);
      background-color: #d9eef2 !important;
    }

    .beacon-cell-action:disabled,
    .beacon-cell-action:disabled:hover {
      transform: none !important;
      box-shadow: none !important;
      background-color: #edf4f5 !important;
    }

    .beacon-grid-colorized .k-table-tbody > tr > td {
      background: linear-gradient(180deg, rgba(250, 254, 255, 0.98) 0%, rgba(240, 250, 252, 0.98) 100%);
      border-bottom: 1px solid #c5dee3;
      border-right: 1px solid #d6e9ed;
      color: #114f58;
      padding: 0.56rem 0.62rem;
      vertical-align: middle;
      transition: background-color 170ms ease, box-shadow 170ms ease;
    }

    .beacon-grid-colorized .k-table-tbody > tr:nth-child(odd) > td {
      background: linear-gradient(180deg, rgba(251, 255, 255, 0.99) 0%, rgba(242, 251, 253, 0.99) 100%);
    }

    .beacon-grid-colorized .k-grid-toolbar {
      width: calc(100% + 2px);
      max-width: none;
      height: 75px;
      min-height: 75px;
      max-height: 75px;
      flex: 0 0 75px;
      box-sizing: border-box;
      border-radius: 0 !important;
      border-left: none !important;
      border-right: none !important;
      margin: 0 -1px;
    }

    .beacon-grid-colorized .k-table-tbody > tr:nth-child(even) > td {
      background: linear-gradient(180deg, rgba(234, 247, 250, 0.98) 0%, rgba(223, 241, 245, 0.98) 100%);
    }

    .beacon-grid-colorized .k-table-tbody > tr:hover > td {
      background: linear-gradient(180deg, #e4f4f7 0%, #d7edf1 100%) !important;
      box-shadow: inset 0 0 0 1px rgba(10, 85, 97, 0.14);
    }

    .beacon-grid-colorized .k-table-tbody > tr.k-state-selected > td,
    .beacon-grid-colorized .k-table-tbody > tr.k-selected > td {
      background: linear-gradient(180deg, #d8edf2 0%, #cde6ed 100%) !important;
      color: #0f4650;
    }

    .beacon-grid-colorized .k-grid-header th {
      background: linear-gradient(180deg, #d4eaf0 0%, #c7e1e8 100%);
      color: #0d4a54;
      border-bottom: 1px solid #9ec5cc;
      border-right: 1px solid #b7d6dc;
      padding: 0.62rem 0.62rem;
    }

    .beacon-grid-colorized .k-grid-header .k-sort-icon,
    .beacon-grid-colorized .k-grid-header .k-svg-icon {
      color: #0f6671;
    }

    .beacon-grid-colorized .beacon-filtered-header .k-grid-header-menu.k-grid-column-menu {
      background: #D4EBEF !important;
      border-radius: 4px;
    }

    .beacon-grid-colorized .k-grid-norecords td {
      padding: 0 !important;
      border-bottom: 1px solid #c5dee3;
    }

    .beacon-grid-colorized .beacon-no-records {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      letter-spacing: 0.01em;
      color: #0f6671;
      background: linear-gradient(180deg, rgba(246, 252, 253, 0.95) 0%, rgba(235, 246, 248, 0.95) 100%);
    }

    @media (prefers-reduced-motion: reduce) {
      .beacon-animated-surface,
      .beacon-animated-toolbar,
      .beacon-animated-toolbar-secondary,
      .beacon-toolbar-actions,
      .beacon-action-btn,
      .beacon-cell-action {
        animation: none !important;
        transition: none !important;
      }
    }

    @media (max-width: 1550px) {
      .beacon-animated-surface {
        animation: beaconFadeUp 260ms ease-out both;
      }
    }

  `;

  const hasVisibleDataColumns = Array.isArray(columns)
    ? columns.some((column: any) => column?.show)
    : columns?.columns?.some((column: any) => column?.show) ?? false;

  const noBeaconsLabel = localizationService.toLanguageString('beacons.no_beacons_present', 'Nessun beacon presente');
  const noRecordsLabel = error || noBeaconsLabel;

  return (
    <div
      style={beaconSurfaceStyle}
      className="beacon-animated-surface"
    >
      <style>{beaconMotionCss}</style>
      {/*{error && <p className="text-danger" style={{ zIndex: 999, position: 'relative', width: '65%' }}>{error}</p>}*/}
      
      {loading && loadingPanel}

      {!hasVisibleDataColumns ?
        <>
          <div style={toolbarStyle} className='beacon-animated-toolbar'>
                      <h3 className="page-title">{localizationService.toLanguageString('beacons.title', 'NOT_FOUND')}</h3>
            <div style={toolbarActionsStyle} className='beacon-toolbar-actions'>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
              <Button
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                svgIcon={fileCsvIcon}
                style={toolbarButtonStyle}
                className='beacon-action-btn'
                disabled={true}
              />
              <Button
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                svgIcon={filePdfIcon}
                style={toolbarButtonStyle}
                className='beacon-action-btn'
                disabled={true}
              />
              <Button
                title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                themeColor={"primary"}
                size="small"
                svgIcon={fileExcelIcon}
                style={toolbarButtonStyle}
                className='beacon-action-btn'
                disabled={true}
              />
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '24px', border: '1px dashed #9ec8cf', borderRadius: '0 0 12px 12px', background: 'linear-gradient(180deg, #f7fcfd 0%, #edf7f9 100%)' }}>
            <p>{localizationService.toLanguageString('beacons.no_visible_columns', 'NOT_FOUND')}</p>
          </div>
        </>
        :
        <>
          <ExcelExport data={beaconsOn} ref={_excelExport}>
            {columns
              .map((col: any) => (
                <ExcelExportColumn
                  key={col.field}
                  field={col.field}
                  title={localizationService.toLanguageString(`beacons.${col.field}`, col.title)}                  
                />
              ))}
          </ExcelExport>
          {/*<ExcelExport data={beaconsOn} ref={_excelExport}>
            <div style={gridWrapperStyle}>*/}
          <Grid
            className={`beacon-grid-colorized${hasActiveGridFilters ? ' grid-filter-active' : ''}`}
            style={{ flex: '1 1 0', height: '100%', minHeight: 0 }}
            data={processedData}
            dataItemKey={"id"}
            cells={{ data: CellRender }}
            take={dataState?.take ?? 10}
            skip={dataState?.skip ?? 0}
            sortable={true}
            reorderable={false}
            pageable={{ type: 'input', buttonCount: 5, pageSizes: [10, 20, 50, 100, localizationService.toLanguageString('beacons.all', 'NOT_FOUND')], pageSizeValue: pageSizeValue, info: true }}
            sort={dataState.sort}
            filter={dataState.filter}
            onPageChange={onPageChange}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            onDataStateChange={onDataStateChange}
            scrollable={'none'}
          >
            <GridNoRecords>
              <div className='beacon-no-records'>{noRecordsLabel}</div>
            </GridNoRecords>
            <GridToolbar style={toolbarStyle} className='beacon-animated-toolbar'>
              <div>
                            <h3 className="page-title">{localizationService.toLanguageString('beacons.title', 'NOT_FOUND')}</h3>
              </div>
              <div style={toolbarActionsStyle} className='beacon-toolbar-actions'>
                <div>
                  <Button
                    type="button"
                    rounded="full"
                    fillMode="solid"
                    themeColor="primary"
                    size="small"
                    svgIcon={arrowRotateCwIcon}
                    title={localizationService.toLanguageString('beacons.refresh', 'Refresh grid')}
                    aria-label={localizationService.toLanguageString('beacons.refresh', 'Refresh grid')}
                    onClick={onRefreshGrid}
                    style={toolbarButtonStyle}
                    className='beacon-action-btn'
                    disabled={loading}
                  />
                </div>
                <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />

                <div>
                  <Button
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                    svgIcon={fileCsvIcon}
                    onClick={() => exportRecords('csv')}
                    style={toolbarButtonStyle}
                    className='beacon-action-btn'
                  />
                </div>
                <div>
                  <Button
                    onClick={() => exportRecords('pdf')}
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                    svgIcon={filePdfIcon}
                    style={toolbarButtonStyle}
                    className='beacon-action-btn'
                  />
                </div>
                <div>
                  <Button
                    title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                    themeColor={"primary"}
                    size="small"
                    svgIcon={fileExcelIcon}
                    onClick={() => exportRecords('excel')}
                    style={toolbarButtonStyle}
                    className='beacon-action-btn'
                  />
                </div>
              </div>
            </GridToolbar>
            {hasVisibleDataColumns && (
              <Column
                cells={{ data: (props) => <LinkMapCell {...props} /> }}
                width={50}
                editable={false}
                filterable={false}
                sortable={false}
              />
            )}
            {
              columns?.length > 0 ?
                columns?.map((column: any, index: any) =>
                  column?.show && (
                    column.field == 'lastSeen' ?
                      <Column
                        key={`beacons-col-${column.field ?? index}`}
                        field='lastSeen'
                        title={localizationService.toLanguageString('beacons.lastSeen', 'NOT_FOUND')}
                        editable={false}
                        filterable={true}
                        filter={'date'}
                        format={column.format}
                        sortable={true}
                        columnMenu={(props) => (
                          <GridColumnMenuFilter
                            {...props}
                            alwaysExpand
                            filterUI={CustomGridFilterDateTime}
                            onExpandChange={() => { }}
                            onCloseMenu={() => {
                              const filterName = props.column.field;
                              setFilterName((prevFilterName: any) => {
                                return prevFilterName.filter((item: any) => item !== filterName);
                              });
                              localStorage.removeItem('filterOpenedColumn');
                            }}
                          />
                        )}
                        cells={{
                          data: (props) => <DateTimeCellWithBackGroundComponent  {...props} />,
                          headerCell: IconHeaderCell
                        }}
                      />
                      :
                      column.field == 'battery' ?
                        <Column
                          key={`beacons-col-${column.field ?? index}`}
                          field='battery'
                          title={localizationService.toLanguageString('beacons.battery', 'NOT_FOUND')}
                          editable={false}
                          filterable={true}
                          columnMenu={ColumnMenuNumericFilter}
                          filter={column?.filter}
                          format={column?.format}
                          sortable={true}
                          cells={{
                            data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                            headerCell: IconHeaderCell
                          }}
                        />
                        :
                        <Column
                          key={`beacons-col-${column.field ?? index}`}
                          field={column.field}
                          title={column.title}
                          filterable={column?.filterable}
                          columnMenu={ColumnMenuCheckboxFilter}
                          className='k-text-center'
                          filter={column?.filter}
                          format={column?.format}
                          sortable={true}
                          width={'2000'}
                          editable={column?.editable}
                          cells={{ headerCell: IconHeaderCell }}
                        />
                  )
                )
                :
                columns?.columns?.map((column: any, index: any) =>
                  columns?.show && (
                    column.field == 'battery' ?
                      <Column
                        key={`beacons-fallback-col-${column.field ?? index}`}
                        field='battery'
                        title={localizationService.toLanguageString('beacons.battery', 'NOT_FOUND')}
                        editable={false}
                        filterable={true}
                        columnMenu={ColumnMenuNumericFilter}
                        className='k-text-center'
                        filter={column?.filter}
                        format={column?.format}
                        sortable={true}
                        cells={{
                          data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                          headerCell: IconHeaderCell
                        }}
                      />
                      :
                      <Column
                        key={`beacons-fallback-col-${column.field ?? index}`}
                        field={column.field}
                        title={column.title}
                        filterable={column?.filterable}
                        columnMenu={ColumnMenuCheckboxFilter}
                        className='k-text-center'
                        cells={{ headerCell: IconHeaderCell }}
                        filter={column?.filter}
                        sortable={true}
                        format={column?.format}
                        editable={column?.editable}
                      />
                  )
                )
            }
          </Grid>
          {/*</div>
          </ExcelExport>*/}

          <GridPDFExport ref={_pdfExport} margin="1cm">
            {
              columns?.length > 0 ?
                columns?.map((column: any, index: any) => {
                  return (
                    column.field == 'battery' ?
                      <GridColumn
                        key={`beacons-pdf-col-${column.field ?? index}`}
                        field='battery'
                        title={localizationService.toLanguageString('beacons.battery', 'NOT_FOUND')}
                        editable={false}
                        filterable={true}
                        columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                        className='k-text-center'
                        filter={column?.filter}
                        format={column?.format}
                        sortable={true}
                        cells={{
                          data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                          headerCell: IconHeaderCell
                        }}
                      />
                      :
                      <GridColumn
                        key={`beacons-pdf-col-${column.field ?? index}`}
                        field={column.field}
                        title={column.title}
                        filterable={column?.filterable}
                        columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                        className='k-text-center'
                        cells={{ headerCell: IconHeaderCell }}
                        editable={column?.editable}
                      />
                  )
                })
                :
                columns?.columns?.map((column: any, index: any) => {
                  return (
                    column.field == 'battery' ?
                      <GridColumn
                        key={`beacons-pdf-fallback-col-${column.field ?? index}`}
                        field='battery'
                        title={localizationService.toLanguageString('beacons.battery', 'NOT_FOUND')}
                        editable={false}
                        filterable={true}
                        columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                        className='k-text-center'
                        filter={column?.filter}
                        format={column?.format}
                        sortable={true}
                        cells={{
                          data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                          headerCell: IconHeaderCell
                        }}
                      />
                      :
                      <GridColumn
                        key={`beacons-pdf-fallback-col-${column.field ?? index}`}
                        field={column.field}
                        title={column.title}
                        filterable={column?.filterable}
                        columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                        className='k-text-center'
                        cells={{ headerCell: IconHeaderCell }}
                        editable={column?.editable}
                      />
                  )
                })

            }
            <Grid
              className={`beacon-grid-colorized${hasActiveGridFilters ? ' grid-filter-active' : ''}`}
              data={processedData}
              dataItemKey={"id"}
              //cellRender={CellRender}
              cells={{ data: CellRender }}
              skip={pageIndex.skip}
              take={pageIndex.take}
              total={totalItems}
              reorderable={false}
              filterable={true}
              sortable={true}
              pageable={{ type: 'input', buttonCount: 5, pageSizes: [5, 10, 50, 100, 'All'], info: true }}
              sort={dataState.sort}
              filter={dataState.filter}
              onPageChange={onPageChange}
              onColumnReorder={onColumnReorder}
              onColumnResize={onColumnResize}
              onDataStateChange={onDataStateChange}
              scrollable={'none'}
            >
              <GridToolbar style={toolbarStyle} className='beacon-animated-toolbar beacon-animated-toolbar-secondary'>
                <div style={{ minWidth: 'fit-content' }}>
                                <h3 className="page-title">{localizationService.toLanguageString('beacons.title', 'NOT_FOUND')}</h3>
                </div>
                <div style={toolbarActionsStyle} className='beacon-toolbar-actions'>
                  <div>
                    <Button
                      title={localizationService.toLanguageString('save_config', 'NOT_FOUND')}
                      themeColor={'primary'}
                      size="small"
                      svgIcon={saveIcon}
                      onClick={saveStateToLocalStorage}
                      style={toolbarButtonStyle}
                      className='beacon-action-btn'
                    >
                    </Button>
                  </div>
                  <div>
                    <Button
                      themeColor={'primary'}
                      size="small"
                      svgIcon={uploadIcon}
                      onClick={loadStateFromLocalStorage}
                      title={localizationService.toLanguageString('load_config', 'NOT_FOUND')}
                      style={toolbarButtonStyle}
                      className='beacon-action-btn'
                    >
                    </Button>
                  </div>
                  <div>
                    <CSVLink data={json2csv(dataState)} separator={","}>
                      <Button
                        themeColor={"primary"}
                        size="small"
                        title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                        svgIcon={fileCsvIcon}
                        style={toolbarButtonStyle}
                        className='beacon-action-btn'
                      />
                    </CSVLink>
                  </div>
                  <div>
                    <Button
                      onClick={pdfExport}
                      themeColor={"primary"}
                      size="small"
                      title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                      svgIcon={filePdfIcon}
                      style={toolbarButtonStyle}
                      className='beacon-action-btn'
                    />
                  </div>
                  <div>
                    <Button
                      title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                      themeColor={"primary"}
                      size="small"
                      svgIcon={fileExcelIcon}
                      onClick={excelExport}
                      style={toolbarButtonStyle}
                      className='beacon-action-btn'
                    />
                  </div>
                  <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
                </div>
              </GridToolbar>
              {hasVisibleDataColumns && (
                <Column
                  cells={{
                    data: (props: any) => <LinkMapCell {...props} />
                  }}
                  width={75}
                  editable={false}
                  filterable={false}
                />
              )}
              {
                columns.length > 0 ?
                  columns?.map((column: any, index: any) =>
                    column.show && (
                      column.field == 'battery' ?
                        <GridColumn
                          key={`beacons-pdf-grid-col-${column.field ?? index}`}
                          field='battery'
                          title={localizationService.toLanguageString('beacons.battery', 'NOT_FOUND')}
                          editable={false}
                          filterable={true}
                          columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                          className='k-text-center'
                          filter={column?.filter}
                          format={column?.format}
                          sortable={true}
                          cells={{
                            data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                            headerCell: IconHeaderCell
                          }}
                        />
                        :
                        <GridColumn
                          key={`beacons-pdf-grid-col-${column.field ?? index}`}
                          field={column.field}
                          title={column.title}
                          filterable={column?.filterable}
                          columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                          className='k-text-center'
                          cells={{ headerCell: IconHeaderCell }}
                          filter={column?.filter}
                          format={column?.format}
                          editable={column?.editable}
                        />
                    )
                  )
                  :
                  columns?.columns?.map((column: any, index: any) =>
                    columns.show && (
                      column.field == 'battery' ?
                        <GridColumn
                          key={`beacons-pdf-grid-fallback-col-${column.field ?? index}`}
                          field='battery'
                          title={localizationService.toLanguageString('beacons.battery', 'NOT_FOUND')}
                          editable={false}
                          filterable={true}
                          columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                          className='k-text-center'
                          filter={column?.filter}
                          format={column?.format}
                          sortable={true}
                          cells={{
                            data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                            headerCell: IconHeaderCell
                          }}
                        />
                        :
                        <GridColumn
                          key={`beacons-pdf-grid-fallback-col-${column.field ?? index}`}
                          field={column.field}
                          title={column.title}
                          filterable={column?.filterable}
                          columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                          className='k-text-center'
                          cells={{ headerCell: IconHeaderCell }}
                          filter={column?.filter}
                          format={column?.format}
                          editable={column?.editable}
                        />
                    )
                  )
              }
            </Grid>
          </GridPDFExport>
        </>
      }
    </div>
  );
};